/**
 * Les routes poussées de l'espace élève que le socle a pontées, rangées par lot.
 *
 * Une route de cette table rend son écran legacy tant que son lot n'a rien inscrit pour elle dans
 * son `routes.ts` ; une fois inscrite, elle rend l'écran des ports — interrupteur allumé, pour un
 * élève, hors espace parent, et hors live sauf si l'entrée déclare qu'elle vit en live
 * (`studentSpaceBridge`). Le pont, lui, est posé une fois pour toutes dans `navigationScreens.js` sur
 * chacune de ces routes : un lot n'a qu'à remplir son `routes.ts`, il ne touche ni la table de
 * navigation ni le pont.
 *
 * Ce qui n'y est pas reste sur l'ancien code, poussé en plein écran : l'éditeur d'avatar,
 * `edit-email`, les absences et rattrapages, les offres, `r`, et, dans le
 * live, tout ce que l'élève ne traverse pas. Une route de plus se pose ici ET dans
 * `navigationScreens.js` (le test `studentSpaceRoutes.test.ts` vérifie les deux côtés).
 *
 * Deux routes qui montent le même écran legacy (`shop` et `petals` → `MyPetalsTabScreen`) sont
 * pontées chacune sous son nom : le lot les inscrit toutes les deux. Une route qui a plusieurs
 * titres (`activity` et `congrats` : hors live et en live) n'a qu'un pont, qui sert ses titres.
 */
import type React from "react"

export const STUDENT_SPACE_ROUTES_BY_LOT = {
    // Accueil : l'onglet seulement — aucune route poussée à ce jour.
    home: [],
    // Activités : les réalisations (la liste complète et les archivées restent sur l'ancien code).
    activities: ["realizations"],
    // Messages (ex-« Mon tutorat ») : « Tous mes lives », route partagée avec le parent et le tuteur, qui gardent la leur.
    tutoring: ["live-sessions-list"],
    // Messagerie avec le tuteur (sans l'enregistreur vocal à l'état « équipe »).
    messaging: ["messenger"],
    // Compte et paramètres, et « Valider l'email » (pontée par ce lot le 20/10 : « Mon compte » la pousse).
    account: ["account", "settings", "account-password", "account-close", "validate-email"],
    // Jeu : la boutique (deux noms), le classement, les quêtes (deux écrans).
    game: ["shop", "petals", "ranking", "skill-sets", "skill-set"],
    // Contrôles.
    assessments: ["school-assessments"],
    // L'exécution d'une activité : l'activité (hors live et en live : `ActivityWrapperScreen`, qui
    // porte aussi la correction — elle n'a pas de route à elle), Sam sur l'activité, les félicitations
    // (hors live et en live).
    activity: ["activity", "sam-session", "congrats"],
    // L'entrée du live : l'avant-séance (le test du matériel et « Rejoindre »), poussée hors live
    // par « GO ! ».
    liveEntry: ["pre-call"],
    // Le live : son écran (la route `menu` sous le titre `live`, `LiveMainLayout` — le pont n'est posé
    // que sous ce titre, l'onglet hors live a sa propre bascule), la fin de séance de l'élève (`live-end`,
    // et la route de l'ancien live), le merci après sa note (`live-satisfaction`, poussé par la note du
    // live), et le quiz de fin de séance (`live-quiz`, poussé à la sortie du live).
    live: ["menu", "end-session-student", "live-end", "live-satisfaction", "live-quiz"],
} as const satisfies Record<string, readonly string[]>

export type StudentSpaceLot = keyof typeof STUDENT_SPACE_ROUTES_BY_LOT

/** Le nom d'une route pontée — tous lots confondus. */
export type StudentSpaceRouteName = (typeof STUDENT_SPACE_ROUTES_BY_LOT)[StudentSpaceLot][number]

/** Les routes d'UN lot : son `routes.ts` ne peut inscrire que celles-là. */
export type StudentSpaceLotRouteName<Lot extends StudentSpaceLot> = (typeof STUDENT_SPACE_ROUTES_BY_LOT)[Lot][number]

/** Ce qu'un écran des ports reçoit de la route : comme les autres écrans des ports, `route` seul. */
export type StudentSpaceScreenProps = {
    // La forme de `route` dépend de chaque écran (ses `params`) ; React Navigation la donne non typée ici.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    route: any
}

export type StudentSpaceScreen = React.ComponentType<StudentSpaceScreenProps>

/** Une entrée qui vit EN LIVE : son écran est rendu hors live comme en live (`livesInLive`). */
export type StudentSpaceLiveEntry = { screen: StudentSpaceScreen; inLive: true }

/**
 * Une entrée du registre : l'écran seul, rendu hors live seulement (le live garde son legacy), ou
 * l'écran qui vit aussi en live (`livesInLive(screen)`).
 */
export type StudentSpaceRouteEntry = StudentSpaceScreen | StudentSpaceLiveEntry

/** La table d'un lot : route → entrée. Vide au socle. */
export type StudentSpaceRouteTable<Lot extends StudentSpaceLot> = Partial<
    Record<StudentSpaceLotRouteName<Lot>, StudentSpaceRouteEntry>
>

/**
 * Déclare qu'une entrée vit EN LIVE : le pont la rend aussi quand le live est ouvert. Sans elle, une
 * route inscrite rend son legacy dès que le live s'ouvre. Une route qui n'existe qu'en live (`menu`
 * sous le titre `live`, `end-session-student`) doit le déclarer pour être rendue.
 */
export function livesInLive(screen: StudentSpaceScreen): StudentSpaceLiveEntry {
    return { screen, inLive: true }
}

function isLiveEntry(entry: StudentSpaceRouteEntry): entry is StudentSpaceLiveEntry {
    return typeof entry === "object" && entry !== null && "inLive" in entry && "screen" in entry
}

/** L'écran d'une entrée, et si elle vit en live. */
export function readRouteEntry(entry: StudentSpaceRouteEntry): { screen: StudentSpaceScreen; inLive: boolean } {
    if (!isLiveEntry(entry)) return { screen: entry, inLive: false }
    return { screen: entry.screen, inLive: entry.inLive }
}
